import { useState, useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { listOrders } from '../api/orders'
import { getCachedCustomStatuses } from '../api/customStatuses'
import { useLangPrefix } from '../hooks/useLangPrefix'
import DesktopShell from '../components/layout/DesktopShell'
import AppShell from '../components/layout/AppShell'
import useManagerStore from '../store/managerStore'
import { formatMoney } from '../lib/money'
import { useTenantCurrencyState } from '../hooks/useTenantCurrency'

const STATUS_COLORS = {
    pending:     { bg: '#fef9c3', color: '#854d0e' },
    confirmed:   { bg: '#dcfce7', color: '#16a34a' },
    in_progress: { bg: '#dbeafe', color: '#1d4ed8' },
    completed:   { bg: '#f0fdf4', color: '#15803d' },
    cancelled:   { bg: '#fee2e2', color: '#dc2626' },
    refunded:    { bg: '#f3f4f6', color: '#6b7280' },
    no_show:     { bg: '#fce7f3', color: '#be185d' },
}

const PAYMENT_COLORS = {
    unpaid:        { bg: '#fee2e2', color: '#dc2626' },
    deposit_paid:  { bg: '#fef9c3', color: '#854d0e' },
    partial:       { bg: '#ffedd5', color: '#c2410c' },
    paid:          { bg: '#dcfce7', color: '#16a34a' },
    refunded:      { bg: '#f3f4f6', color: '#6b7280' },
    waived:        { bg: '#ede9fe', color: '#7c3aed' },
}

// An order keeps the currency it was created in (BE C14 `orders.currency`);
// legacy orders without one are read in the tenant currency.
const getOrderCurrency = (order) => (typeof order?.currency === 'string' && order.currency.trim() ? order.currency : null)
const formatDate = d => d ? new Date(d).toLocaleString('vi-VN', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '-'

function StatusPill({ status, map, label }) {
    const s = map[status] || { bg: '#f3f4f6', color: '#666' }
    return (
        <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
            {label}
        </span>
    )
}

function hexToBg(hex) {
    const h = String(hex || '').replace('#', '')
    if (!/^[0-9a-f]{6}$/i.test(h)) return null
    const n = parseInt(h, 16)
    const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255
    return `rgba(${r}, ${g}, ${b}, 0.12)`
}

export default function OrdersPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { prefix, lang } = useLangPrefix()
    const tenantId = useManagerStore((s) => s.tenantId)
    const isDesktop = window.innerWidth >= 1024
    const { currency: tenantCurrency, isResolved: currencyResolved } = useTenantCurrencyState()
    // Placeholder only when the order has no currency and the tenant one is unresolved.
    const formatOrderTotal = (order) => {
        const currency = getOrderCurrency(order) ?? (currencyResolved ? tenantCurrency : null)
        if (!currency) return <span role="status" aria-label={t('common.loading')}>…</span>
        return formatMoney(order.final_total, currency, lang)
    }

    const [orders, setOrders] = useState([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [filterStatus, setFilterStatus] = useState('')
    const [filterType, setFilterType] = useState('')
    const [filterPayment, setFilterPayment] = useState('')
    const [page, setPage] = useState(0)
    const limit = 20
    const [customOrderStatuses, setCustomOrderStatuses] = useState([])

    useEffect(() => {
        if (!tenantId) {
            setCustomOrderStatuses([])
            return
        }
        let cancelled = false
        getCachedCustomStatuses(tenantId, 'order')
            .then(items => { if (!cancelled) setCustomOrderStatuses(Array.isArray(items) ? items : []) })
            .catch(err => Sentry.captureException(err, { tags: { action: 'fetch_order_custom_statuses' } }))
        return () => { cancelled = true }
    }, [tenantId])

    // Custom (non-default) statuses only — default statuses keep their existing
    // hardcoded label/color below so this merge never overrides them with a
    // null color coming back from the API's lazy-seeded default rows.
    const customOnlyStatuses = useMemo(
        () => customOrderStatuses.filter(s => !s.is_default),
        [customOrderStatuses],
    )
    const orderStatusMap = useMemo(() => {
        const map = { ...STATUS_COLORS }
        customOnlyStatuses.forEach(s => {
            const bg = hexToBg(s.color) || '#f3f4f6'
            map[s.slug] = { bg, color: s.color || '#666' }
        })
        return map
    }, [customOnlyStatuses])
    const orderStatusOptions = useMemo(
        () => [
            ...Object.keys(STATUS_COLORS),
            ...customOnlyStatuses.map(s => s.slug),
        ],
        [customOnlyStatuses],
    )
    function orderStatusLabel(slug) {
        const custom = customOnlyStatuses.find(s => s.slug === slug)
        if (custom) return custom.label
        return t(`orders.orderStatus.${slug}`, { defaultValue: slug?.replace(/_/g, ' ') })
    }

    async function fetchOrders() {
        if (!tenantId) {
            setOrders([])
            setTotal(0)
            setLoading(false)
            return
        }
        setLoading(true)
        try {
            const params = { limit, offset: page * limit }
            if (filterStatus) params.status = filterStatus
            if (filterType) params.order_type = filterType
            if (filterPayment) params.payment_status = filterPayment
            const data = await listOrders(tenantId, params)
            setOrders(data.orders || [])
            setTotal(data.total || 0)
        } catch (e) {
            Sentry.captureException(e, { tags: { action: 'fetch_orders' } })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { fetchOrders() }, [tenantId, filterStatus, filterType, filterPayment, page])

    const filtered = orders.filter(o =>
        !search ||
        (o.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (o.customer_phone || '').includes(search) ||
        o.id.includes(search)
    )

    const content = (
        <div style={{ padding: isDesktop ? '24px 32px' : '12px 12px 80px 12px', background: 'var(--gc-page-bg)', minHeight: '100vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <div style={{ fontSize: isDesktop ? 22 : 18, fontWeight: 700, color: '#111' }}>{t('orders.title')}</div>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{t('orders.totalOrders', { count: total })}</div>
                </div>
                <button onClick={() => navigate(`${prefix}/orders/new`)}
                    style={{ padding: '8px 18px', background: 'var(--gc-primary)', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                    {t('orders.newOrder')}
                </button>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder={t('orders.searchPlaceholder')}
                    style={{ flex: 1, minWidth: 180, padding: '8px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, background: 'white', outline: 'none' }} />
                <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0) }}
                    style={{ padding: '8px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, background: 'white', outline: 'none' }}>
                    <option value="">{t('orders.allStatus')}</option>
                    {orderStatusOptions.map(s => <option key={s} value={s}>{orderStatusLabel(s)}</option>)}
                </select>
                <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(0) }}
                    style={{ padding: '8px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, background: 'white', outline: 'none' }}>
                    <option value="">{t('orders.allTypes')}</option>
                    {['table_order', 'appointment', 'room_order', 'tour_booking', 'enrollment'].map(k => <option key={k} value={k}>{t(`orders.orderType.${k}`)}</option>)}
                </select>
                <select value={filterPayment} onChange={e => { setFilterPayment(e.target.value); setPage(0) }}
                    style={{ padding: '8px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, background: 'white', outline: 'none' }}>
                    <option value="">{t('orders.allPayments')}</option>
                    {Object.keys(PAYMENT_COLORS).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 48, color: '#999' }}>{t('orders.loading')}</div>
            ) : isDesktop ? (
                <div style={{ background: 'white', borderRadius: 14, border: '0.5px solid #e0e3f0', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f8f9ff' }}>
                                {[
                                    t('orders.thOrderId'),
                                    t('orders.thCustomer'),
                                    t('orders.thType'),
                                    t('orders.thItems'),
                                    t('orders.thTotal'),
                                    t('orders.thStatus'),
                                    t('orders.thPayment'),
                                    t('orders.thDate'),
                                    '',
                                ].map((h, i) => (
                                    <th key={i} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', borderBottom: '0.5px solid #e0e3f0', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((o, i) => (
                                <tr key={o.id} style={{ borderBottom: i < filtered.length - 1 ? '0.5px solid #f0f0f0' : 'none', cursor: 'pointer' }}
                                    onClick={() => navigate(`${prefix}/orders/${o.id}`)}>
                                    <td style={{ padding: '12px 14px', fontSize: 12, fontFamily: 'monospace', color: '#666' }}>{o.id.slice(0, 8).toUpperCase()}</td>
                                    <td style={{ padding: '12px 14px' }}>
                                        <div style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>{o.customer_name || '—'}</div>
                                        <div style={{ fontSize: 11, color: '#999' }}>{o.customer_phone || ''}</div>
                                    </td>
                                    <td style={{ padding: '12px 14px', fontSize: 12, color: '#666' }}>{o.order_type ? t(`orders.orderType.${o.order_type}`, { defaultValue: o.order_type }) : ''}</td>
                                    <td style={{ padding: '12px 14px', fontSize: 13, color: '#333' }}>{t('orders.itemsCount', { count: o.line_items?.length || 0 })}</td>
                                    <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: 'var(--gc-primary)' }}>{formatOrderTotal(o)}</td>
                                    <td style={{ padding: '12px 14px' }}><StatusPill status={o.status} map={orderStatusMap} label={orderStatusLabel(o.status)} /></td>
                                    <td style={{ padding: '12px 14px' }}><StatusPill status={o.payment_status} map={PAYMENT_COLORS} label={t(`orders.paymentStatusEnum.${o.payment_status}`, { defaultValue: o.payment_status?.replace(/_/g, ' ') })} /></td>
                                    <td style={{ padding: '12px 14px', fontSize: 11, color: '#999', whiteSpace: 'nowrap' }}>{formatDate(o.created_at)}</td>
                                    <td style={{ padding: '12px 14px' }}>
                                        <button onClick={e => { e.stopPropagation(); navigate(`${prefix}/orders/${o.id}`) }}
                                            style={{ padding: '5px 12px', border: '0.5px solid #e0e3f0', borderRadius: 6, background: 'white', fontSize: 12, cursor: 'pointer', color: '#333' }}>
                                            {t('common.view')}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filtered.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: '#999', fontSize: 13 }}>{t('orders.noOrders')}</div>}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '0.5px solid #f0f0f0' }}>
                        <div style={{ fontSize: 12, color: '#999' }}>{t('orders.showing', { from: page * limit + 1, to: Math.min((page + 1) * limit, total), total })}</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                                style={{ padding: '5px 12px', border: '0.5px solid #e0e3f0', borderRadius: 6, background: 'white', fontSize: 12, cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.5 : 1 }}>{t('orders.prev')}</button>
                            <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total}
                                style={{ padding: '5px 12px', border: '0.5px solid #e0e3f0', borderRadius: 6, background: 'white', fontSize: 12, cursor: (page + 1) * limit >= total ? 'not-allowed' : 'pointer', opacity: (page + 1) * limit >= total ? 0.5 : 1 }}>{t('orders.next')}</button>
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {filtered.map(o => (
                        <div key={o.id} onClick={() => navigate(`${prefix}/orders/${o.id}`)}
                            style={{ background: 'white', borderRadius: 12, border: '0.5px solid #e0e3f0', padding: 14, cursor: 'pointer' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                <div>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{o.customer_name || '—'}</div>
                                    <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{o.customer_phone || ''}</div>
                                </div>
                                <StatusPill status={o.status} map={orderStatusMap} label={orderStatusLabel(o.status)} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontSize: 12, color: '#666' }}>{o.order_type ? t(`orders.orderType.${o.order_type}`, { defaultValue: o.order_type }) : ''} · {t('orders.itemsCount', { count: o.line_items?.length || 0 })}</div>
                                    <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{formatDate(o.created_at)}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--gc-primary)' }}>{formatOrderTotal(o)}</div>
                                    <StatusPill status={o.payment_status} map={PAYMENT_COLORS} label={t(`orders.paymentStatusEnum.${o.payment_status}`, { defaultValue: o.payment_status?.replace(/_/g, ' ') })} />
                                </div>
                            </div>
                        </div>
                    ))}
                    {filtered.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: '#999', fontSize: 13 }}>{t('orders.noOrders')}</div>}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', padding: '8px 0' }}>
                        <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                            style={{ padding: '7px 16px', border: '0.5px solid #e0e3f0', borderRadius: 8, background: 'white', fontSize: 13, cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.5 : 1 }}>{t('orders.prev')}</button>
                        <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total}
                            style={{ padding: '7px 16px', border: '0.5px solid #e0e3f0', borderRadius: 8, background: 'white', fontSize: 13, cursor: (page + 1) * limit >= total ? 'not-allowed' : 'pointer', opacity: (page + 1) * limit >= total ? 0.5 : 1 }}>{t('orders.next')}</button>
                    </div>
                </div>
            )}
        </div>
    )

    return isDesktop
        ? <DesktopShell>{content}</DesktopShell>
        : <AppShell activeTab="orders">{content}</AppShell>
}
